import type { UseImageEditorResult } from '../../hooks/useImageEditor';

interface AdjustmentPanelProps {
  editor: UseImageEditorResult;
}

/**
 * 亮度／對比／飽和度調整區塊。
 *
 * 調整值即時反映於畫布預覽（CSS filter），輸出時再以相同參數套用至結果圖。
 */
export function AdjustmentPanel({ editor }: AdjustmentPanelProps) {
  const {
    brightness,
    contrast,
    saturation,
    setBrightness,
    setContrast,
    setSaturation,
  } = editor;

  const handleChange =
    (setter: (value: number) => void) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(event.target.value);
      if (!Number.isFinite(value)) return;
      setter(Math.max(-100, Math.min(100, value)));
    };

  return (
    <div className="space-y-3">
      <div>
        <div className="mb-1 flex items-center justify-between text-xs">
          <span className="text-gray-600 dark:text-gray-400">亮度</span>
          <span className="text-gray-500 dark:text-gray-400">{brightness}</span>
        </div>
        <input
          type="range"
          min={-100}
          max={100}
          step={1}
          value={brightness}
          onChange={handleChange(setBrightness)}
          className="w-full accent-primary"
        />
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between text-xs">
          <span className="text-gray-600 dark:text-gray-400">對比</span>
          <span className="text-gray-500 dark:text-gray-400">{contrast}</span>
        </div>
        <input
          type="range"
          min={-100}
          max={100}
          step={1}
          value={contrast}
          onChange={handleChange(setContrast)}
          className="w-full accent-primary"
        />
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between text-xs">
          <span className="text-gray-600 dark:text-gray-400">飽和度</span>
          <span className="text-gray-500 dark:text-gray-400">{saturation}</span>
        </div>
        <input
          type="range"
          min={-100}
          max={100}
          step={1}
          value={saturation}
          onChange={handleChange(setSaturation)}
          className="w-full accent-primary"
        />
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        即時反映於上方預覽；按下「輸出並下載」時一併套用至輸出檔。
      </p>
    </div>
  );
}
